import type {
  ColumnDef,
  ColumnFilter,
  RowPinningState,
} from "@tanstack/react-table";
import { type PropsWithChildren, useMemo, useState } from "react";

import type { InvoiceSearchResult } from "@/apis/gql/types";
import { TableInstanceProvider } from "@/components/shared/data-table";

const tableName = "transactions-panel-match-tab";

type Props = PropsWithChildren<{
  data: InvoiceSearchResult[] | undefined;
  columns: ColumnDef<InvoiceSearchResult>[];
  initialColumnFilters?: ColumnFilter[];
  showConfidences?: boolean;
  clickableColumns?: string[];
  onRowSelectionChange: (value: Record<string, boolean>) => void;
  rowSelection: Record<string, boolean>;
  rowPinning: RowPinningState;
  onRowPinningChange: (value: RowPinningState) => void;
  footer: React.ReactNode | false;
}>;

export const InvoiceMatchingTableInstanceProvider = ({
  data: _data,
  columns,
  children,
  onRowSelectionChange,
  rowSelection,
  rowPinning,
  onRowPinningChange,
  footer,
}: Props) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);

  const data = useMemo(() => {
    if (!_data) return [];

    return _data;
  }, [_data]);

  return (
    <TableInstanceProvider
      data={data}
      columns={columns}
      tableName={tableName}
      getRowId={(row) => {
        return row.invoiceId;
      }}
      globalFilter={globalFilter}
      onGlobalFilterChange={setGlobalFilter}
      columnFilters={columnFilters}
      onColumnFilterChange={setColumnFilters}
      initialColumnVisibility={{
        invoiceNo: false,
        invoiceTotalAmount: false,
        supplierName: false,
        invoiceDate: false,
      }}
      initialSorting={[{ id: "invoiceData", desc: false }]}
      onRowSelectionChange={onRowSelectionChange}
      rowSelection={rowSelection}
      rowPinning={rowPinning}
      onRowPinningChange={onRowPinningChange}
      focusedRowUrlParamName="invoice-id"
      footer={footer}
    >
      {children}
    </TableInstanceProvider>
  );
};
